import React, { createContext, ReactNode, useEffect, useState } from "react"

import { IsoLocale } from "@izettle/constants"
import { getLocale } from "@izettle/react"

import { LocaleOptions, Translator, TranslatorParams } from "../translator"

export type TenkoReplacement = string | React.ReactElement | React.ReactNode

export type TenkoTranslation = string | React.ReactNode[]

export type TenkoTextFunction = <R extends TenkoReplacement = string>(
  key: string,
  replacements?: R[],
) => R extends string ? string : ReactNode[]

export type ITranslatorContext = {
  translator: Translator
  text: TenkoTextFunction
  /** @deprecated Simply pass the overriding `IsoLocale` directly into `TranslationsProvider` */
  setLocale: (locale: IsoLocale) => void
}

const replacementIndexRegex = /{(\d+)}/g
const replacementRegex = /({\d+})/
export const replacePlaceholders: TenkoTextFunction = (text, replacements = []) => {
  if (replacements.length < 1) {
    return text
  }

  // Simple regex replace if it's all strings
  if (
    replacements.every((r) => typeof r === "string" || typeof r === "number" || r == null)
  ) {
    return (
      text
        // Replace with replacement or nothing if no replacement is found
        .replace(replacementIndexRegex, (_: string, replacementIndex: string) => {
          return (replacements[Number(replacementIndex[0])] ?? "") as string
        })
    )
  }

  // Otherwise split into array and replace with strings and jsx elements
  return text.split(replacementRegex).map((str) => {
    const match = replacementRegex.exec(str)
    if (!match) {
      return str
    }

    const replacementIndex = /\d+/.exec(match[0])![0]
    return replacements[Number(replacementIndex)]
  }) as any
}

const getLocaleOptions = (
  ...inputs: Array<IsoLocale | LocaleOptions | null | undefined>
): LocaleOptions => {
  for (let i = 0; i < inputs.length; i++) {
    const input = inputs[i]

    if (input == null) {
      // eslint-disable-next-line no-continue
      continue
    }

    if (typeof input === "string") {
      return Translator.optionsFromIsoLocale(input)
    }

    return input
  }

  throw new Error("Could not get locale options!")
}

export const TranslatorContext = createContext<ITranslatorContext>(null as any)

type TranslationsProviderProps = {
  /** Override any inferred locale. */
  locale?: IsoLocale | LocaleOptions
  allowedLocales?: IsoLocale[]
  defaultLocale?: IsoLocale
  children: ReactNode
  onLoadTranslationsError?: (error: Error) => void
} & Pick<
  TranslatorParams,
  "projectName" | "getTranslationsUrl" | "fallbackDisabled" | "onTranslationMissing"
>

export const TranslationsProvider = ({
  locale: localeOverride,
  defaultLocale,
  allowedLocales,
  children,
  onLoadTranslationsError,
  ...translatorProps
}: TranslationsProviderProps) => {
  const [internalOverride, setInternalOverride] = useState<IsoLocale | null>(null)
  const [contextValue, setContextValue] = useState<ITranslatorContext | null>(null)

  const resolvedLocale = getLocaleOptions(
    internalOverride,
    localeOverride,
    getLocale(window.navigator.languages, allowedLocales),
    defaultLocale,
  )

  useEffect(() => {
    const translator = new Translator({
      ...translatorProps,
      locale: resolvedLocale,
    })

    void translator
      .loadTranslations()
      .then(() =>
        setContextValue({
          translator,
          setLocale: setInternalOverride,
          text: (key, replacements) =>
            replacePlaceholders(translator.translate(key), replacements),
        }),
      )
      .catch(onLoadTranslationsError)
  }, [resolvedLocale.language, resolvedLocale.country, resolvedLocale.currency])

  return contextValue ? (
    <TranslatorContext.Provider value={contextValue}>
      {children}
    </TranslatorContext.Provider>
  ) : null
}
